import type { Baseball } from "@knowsball/common";
import type { EntityType } from "@/lib/api";

export type Players = Record<string, Baseball.Player>;
export type Teams = Record<string, Baseball.Team>;

export interface EntityOption {
	entityID: string;
	entityType: EntityType;
	group: "Players" | "Teams";
	label: string;
	meta: string;
	search: string;
	value: string;
}

export const playerName = (player: Baseball.Player): string =>
	player.mlbStatsData?.person?.fullName || player.name;

export const teamName = (team: Baseball.Team): string =>
	team.mlbStatsData?.name || team.apiSportsData?.name || team.name;

export const entityName = (
	entityType: EntityType | null | undefined,
	entityID: string | null | undefined,
	players: Players,
	teams: Teams,
): string | null => {
	if (!entityType || !entityID) {
		return null;
	}

	if (entityType === "team") {
		const team = teams[entityID];
		return team ? teamName(team) : humanizeID(entityID);
	}

	const player = players[entityID];
	return player ? playerName(player) : humanizeID(entityID);
};

export const humanizeID = (entityID: string): string =>
	entityID
		.split("-")
		.filter(Boolean)
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(" ");

export const buildEntityOptions = (
	players: Players,
	teams: Teams,
): EntityOption[] => {
	const playerOptions = Object.entries(players).map(([entityID, player]) => {
		const label = playerName(player);

		return {
			entityID,
			entityType: "player" as const,
			group: "Players" as const,
			label,
			meta: "MLB",
			search: [label, player.name, player.lowerCaseName, entityID]
				.filter(Boolean)
				.join(" ")
				.toLowerCase(),
			value: `player:${entityID}`,
		};
	});

	const teamOptions = Object.entries(teams).map(([entityID, team]) => {
		const label = teamName(team);

		return {
			entityID,
			entityType: "team" as const,
			group: "Teams" as const,
			label,
			meta: "MLB",
			search: [label, team.name, team.lowerCaseName, entityID]
				.filter(Boolean)
				.join(" ")
				.toLowerCase(),
			value: `team:${entityID}`,
		};
	});

	return [...playerOptions, ...teamOptions].sort((a, b) =>
		a.label.localeCompare(b.label),
	);
};

export const findEntityOption = (
	options: EntityOption[],
	entityType: EntityType | null,
	entityID: string | null,
): EntityOption | undefined =>
	entityType && entityID
		? options.find(
				(option) =>
					option.entityType === entityType && option.entityID === entityID,
			)
		: undefined;
